// TypeScript interfaces for Work Execution module

export interface WorkExecutionData {
  id: string;
  workOrderId: string;
  workOrderNumber: string;
  workOrderTitle: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  contractorId: string;
  contractorName: string;
  contractType: string;
  workOrderOwner: string;
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  releasedOn?: string;
  totalValue: number;
  executedValue: number;
  paidAmount: number;
  retentionAmount: number;
  onHoldAmount: number;
  outstandingAmount: number;
  progressPercentage: number;
  status: WorkExecutionStatus;
  executionBatches: ExecutionBatch[];
  executionLines?: ExecutionLine[];
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  lastExecutionDate?: string;
  lastExecutionUpdate?: string;
}

export interface ExecutionBatch {
  id: string;
  batchNumber: string;
  workOrderId: string;
  executionPeriodFrom: string;
  executionPeriodTo: string;
  totalExecutedValue: number;
  progressPercentage: number;
  status: ExecutionBatchStatus;
  submittedBy: string;
  submittedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  notes?: string;
  attachments: ExecutionAttachment[];
  executionLines: ExecutionLine[];
  approvalHistory: ExecutionApprovalHistoryItem[];
}

export interface ExecutionLine {
  id: string;
  workOrderLineId: string;
  lineNumber: string;
  taskTitle: string;
  taskDescription?: string;
  approvedQuantity: number;
  executedQuantity: number;
  cumulativeExecuted: number;
  remainingQuantity: number;
  workCompletionPercentage: number;
  rate: number;
  executedValue: number;
  uom: string;
  remarks?: string;
  status?: "Pending" | "In Progress" | "Completed" | "On Hold";
}

export interface ExecutionAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadDate: string;
  uploadedBy: string;
  url?: string;
}

export interface ExecutionApprovalHistoryItem {
  action: string;
  actionBy: string;
  actionDate: string;
  role: ExecutionUserRole;
  comments?: string;
  status: ExecutionBatchStatus;
}

// Enums
export enum WorkExecutionStatus {
  NotStarted = "Not Started",
  InProgress = "In Progress", 
  OnHold = "On Hold",
  Completed = "Completed",
  Closed = "Closed",
  Cancelled = "Cancelled",
  PendingApproval = "Pending Approvals"
}

export enum ExecutionBatchStatus {
  Draft = "Draft",
  Submitted = "Submitted for Approval",
  UnderReview = "Under Review",
  Approved = "Approved",
  Rejected = "Rejected",
  ReturnedForCorrection = "Returned for Correction"
}

export enum ExecutionUserRole {
  Contractor = "Contractor",
  SiteEngineer = "Site Engineer",
  ProjectManager = "Project Manager",
  Checker = "Checker",
  Verifier = "Verifier", 
  Approver = "Approver"
}

// Filter interfaces
export interface WorkExecutionFilters {
  workOrderNumber?: string;
  workOrderTitle?: string;
  siteIds?: string[];
  contractorIds?: string[];
  contractTypes?: string[];
  statuses?: WorkExecutionStatus[];
  progressRange?: { min: number; max: number };
  valueRange?: { min: number; max: number };
  executionDateRange?: [any, any] | null;
  workOrderOwner?: string;
}

export interface ExecutionBatchFilters {
  batchNumber?: string;
  workOrderNumber?: string;
  submittedBy?: string;
  statuses?: ExecutionBatchStatus[];
  submissionDateRange?: [any, any] | null;
  executionPeriodRange?: [any, any] | null;
  valueRange?: { min: number; max: number };
}

// Widget data interfaces
export interface WorkExecutionWidgetData {
  totalWorkExecutions: number;
  notStartedCount: number;
  notStartedPercentage: number;
  inProgressCount: number;
  inProgressPercentage: number;
  onHoldCount: number;
  onHoldPercentage: number;
  completedCount: number;
  completedPercentage: number;
  closedCount: number;
  closedPercentage: number;
  cancelledCount: number;
  cancelledPercentage: number;
  totalWorkExecutionValue: number;
  executedToDate: number;
  pendingExecution: number;
  avgProgressPercentage: number;
  avgTimeForCompletion: number;
}

export interface ExecutionBatchWidgetData {
  totalBatches: number;
  draftCount: number;
  draftPercentage: number;
  submittedCount: number;
  submittedPercentage: number;
  underReviewCount: number;
  underReviewPercentage: number;
  approvedCount: number;
  approvedPercentage: number;
  rejectedCount: number;
  rejectedPercentage: number;
  avgApprovalTime: number;
  avgBatchValue: number;
  totalBatchValue: number;
}

// Component Props interfaces
export interface WorkExecutionTableProps {
  data: WorkExecutionData[];
  loading?: boolean;
  onView?: (record: WorkExecutionData) => void;
  onExecutionUpdate?: (record: WorkExecutionData) => void;
  onHold?: (record: WorkExecutionData) => void;
  onResume?: (record: WorkExecutionData) => void;
  onComplete?: (record: WorkExecutionData) => void;
  onClose?: (record: WorkExecutionData) => void;
}

export interface ExecutionBatchTableProps {
  data: ExecutionBatch[];
  loading?: boolean;
  onView?: (record: ExecutionBatch) => void;
  onApprove?: (record: ExecutionBatch) => void;
  onReject?: (record: ExecutionBatch) => void;
  onReturn?: (record: ExecutionBatch) => void;
}

// Master data interfaces
export interface ExecutionMasterData {
  sites: Array<{ label: string; value: string }>;
  contractors: Array<{ label: string; value: string }>;
  contractTypes: Array<{ label: string; value: string }>;
  workOrderOwners: Array<{ label: string; value: string }>;
  executionStatuses: Array<{ label: string; value: WorkExecutionStatus }>;
  batchStatuses: Array<{ label: string; value: ExecutionBatchStatus }>;
}